import { ENTITY_MODAL_TYPES } from './entity-modal.constants';
import { useTranslation } from 'react-i18next';
import {
  FieldLabel,
  ModalInput,
  ModalTextarea,
  CustomSelect,
  Pill,
  Section,
  TagList,
} from './EntityModalComponents';
import type { EntityFormProps } from './entity-modal.types';

const accent = ENTITY_MODAL_TYPES.task.accent;

const STATUS_PILLS = [
  { value: 'todo', label: 'common.status_todo', color: '#a1a1aa' },
  { value: 'in_progress', label: 'common.status_in_progress', color: '#3b82f6' },
  { value: 'done', label: 'common.status_done', color: '#22c55e' },
] as const;

const PRIORITY_PILLS = [
  { value: 'low', label: 'common.priority_low', color: '#a1a1aa' },
  { value: 'medium', label: 'common.priority_medium', color: '#f59e0b' },
  { value: 'high', label: 'common.priority_high', color: '#ef4444' },
  { value: 'critical', label: 'common.priority_critical', color: '#dc2626' },
] as const;

export function TaskForm({
  isEdit,
  formData,
  onChange,
  domains,
  goals,
  tasks,
  events,
  entityId,
  tags,
  onAddTag,
  onRemoveTag,
}: EntityFormProps) {
  const { t } = useTranslation();
  const domainOptions = domains.map((d) => ({ value: d.id, label: d.name }));
  const goalOptions = [
    { value: '', label: t('common.none_m') },
    ...goals.map((g) => ({ value: g.id, label: g.title })),
  ];
  const eventOptions = [
    { value: '', label: t('common.none_m') },
    ...events.map((e) => ({ value: e.id, label: e.title })),
  ];
  const parentOptions = [
    { value: '', label: t('common.none_f') },
    ...tasks
      .filter((t) => t.id !== entityId && t.parentId !== entityId)
      .map((t) => ({ value: t.id, label: t.title })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <input
        type="text"
        required
        placeholder={t('entity.task_title_placeholder')}
        value={formData.title || ''}
        onChange={(e) => onChange('title', e.target.value)}
        className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
      />

      <div className="flex gap-1.5 flex-wrap items-center">
        {STATUS_PILLS.map((s) => (
          <Pill
            key={s.value}
            label={t(s.label)}
            color={s.color}
            active={formData.status === s.value}
            onClick={() => onChange('status', s.value)}
          />
        ))}
        <div className="w-px h-5 bg-zinc-700/30 mx-1" />
        {PRIORITY_PILLS.map((p) => (
          <Pill
            key={p.value}
            label={t(p.label)}
            color={p.color}
            active={formData.priority === p.value}
            onClick={() => onChange('priority', p.value)}
          />
        ))}
      </div>

      <Section title={t('common.details')} defaultOpen accent={accent}>
        <div className="flex flex-col gap-3.5">
          <div>
            <FieldLabel>{t('common.domain')}</FieldLabel>
            <CustomSelect
              options={domainOptions}
              value={formData.domainId || ''}
              onChange={(val) => onChange('domainId', val)}
            />
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <div>
              <FieldLabel>{t('common.deadline')}</FieldLabel>
              <ModalInput
                type="date"
                value={formData.dueDate ? formData.dueDate.split('T')[0] : ''}
                onChange={(val) =>
                  onChange('dueDate', val ? new Date(val).toISOString() : undefined)
                }
              />
            </div>
            <div>
              <FieldLabel>{t('common.hour')}</FieldLabel>
              <ModalInput
                type="time"
                value={formData.dueTime ?? ''}
                onChange={(val) => onChange('dueTime', val)}
              />
            </div>
            <div>
              <FieldLabel>{t('entity.task_estimated_duration')}</FieldLabel>
              <ModalInput
                placeholder={t('entity.task_duration_placeholder')}
                value={formData.estimatedDuration ?? ''}
                onChange={(val) => onChange('estimatedDuration', val)}
              />
            </div>
          </div>
          <div>
            <FieldLabel>{t('common.description')}</FieldLabel>
            <ModalTextarea
              placeholder={t('entity.task_description_placeholder')}
              value={formData.description || ''}
              onChange={(val) => onChange('description', val)}
            />
          </div>
        </div>
      </Section>

      <Section title={t('common.attachments')} defaultOpen={isEdit} accent={accent}>
        <div className="flex flex-col gap-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <FieldLabel>{t('entity.task_parent')}</FieldLabel>
              <CustomSelect
                options={parentOptions}
                value={formData.parentId || ''}
                onChange={(val) => onChange('parentId', val || null)}
              />
            </div>
            <div>
              <FieldLabel>{t('entity.task_linked_goal')}</FieldLabel>
              <CustomSelect
                options={goalOptions}
                value={formData.goalId || ''}
                onChange={(val) => onChange('goalId', val || null)}
              />
            </div>
          </div>
          <div>
            <FieldLabel>{t('entity.task_linked_event')}</FieldLabel>
            <CustomSelect
              options={eventOptions}
              value={formData.eventId || ''}
              onChange={(val) => onChange('eventId', val || null)}
            />
          </div>
        </div>
      </Section>

      <Section title="Tags" defaultOpen={false} accent={accent}>
        <TagList tags={tags} accent={accent} onAdd={onAddTag} onRemove={onRemoveTag} />
      </Section>

      <div>
        <FieldLabel>{t('common.cost')}</FieldLabel>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder={t('entity.task_cost_placeholder')}
            value={formData.cost ?? ''}
            onChange={(e) =>
              onChange('cost', e.target.value === '' ? null : parseFloat(e.target.value))
            }
            className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition"
          />
          <span className="text-sm text-zinc-500">€</span>
        </div>
      </div>
    </div>
  );
}
